import type { Permission, QueryWarning, ViewCreate, ViewDto, ViewPatch } from "@plannello/shared";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { getCatalog } from "@/data/CatalogProvider";
import { api } from "@/lib/api";
import { permissionsQuery } from "@/lib/permissions";
import { queryClient } from "@/query-client";

/**
 * Saved views API (server: apps/server/src/routes/views.ts). Lists are cached per product and
 * for the sidebar pins; every write refreshes both, so the views menu and the sidebar never
 * disagree.
 */

export const productViewsQuery = (productKey: string) =>
  queryOptions({
    queryKey: ["views", "product", productKey.toUpperCase()],
    queryFn: ({ signal }) => api.get<ViewDto[]>(`/api/v1/products/${productKey}/views`, signal),
    staleTime: 60_000,
  });

export const pinnedViewsQuery = queryOptions({
  queryKey: ["views", "pinned"],
  queryFn: ({ signal }) => api.get<ViewDto[]>("/api/v1/views/pinned", signal),
  staleTime: 60_000,
});

export function useProductViews(productKey: string | undefined) {
  return useQuery({ ...productViewsQuery(productKey ?? ""), enabled: !!productKey });
}

export function usePinnedViews() {
  return useQuery(pinnedViewsQuery);
}

export const refreshViews = () => queryClient.invalidateQueries({ queryKey: ["views"] });

/** Replaces one view in every cached list (optimistic pin, rename). */
function patchCached(id: string, fn: (v: ViewDto) => ViewDto) {
  queryClient.setQueriesData<ViewDto[]>({ queryKey: ["views", "product"] }, (old) =>
    old?.map((v) => (v.id === id ? fn(v) : v)),
  );
}

export async function createView(input: ViewCreate): Promise<ViewDto> {
  const v = await api.post<ViewDto>("/api/v1/views", input);
  await refreshViews();
  return v;
}

export async function patchView(id: string, patch: ViewPatch): Promise<ViewDto> {
  const v = await api.patch<ViewDto>(`/api/v1/views/${encodeURIComponent(id)}`, patch);
  patchCached(id, () => v);
  void refreshViews();
  return v;
}

export async function deleteView(id: string): Promise<void> {
  queryClient.setQueriesData<ViewDto[]>({ queryKey: ["views"] }, (old) => old?.filter((v) => v.id !== id));
  try {
    await api.delete(`/api/v1/views/${encodeURIComponent(id)}`);
  } finally {
    void refreshViews();
  }
}

/** Pins or unpins (built-in ids work too). Optimistic: the sidebar updates at once. */
export async function setPinned(view: ViewDto, pinned: boolean): Promise<void> {
  patchCached(view.id, (v) => ({ ...v, pinned }));
  queryClient.setQueryData<ViewDto[]>(pinnedViewsQuery.queryKey, (old) =>
    old
      ? pinned
        ? [...old.filter((v) => v.id !== view.id), { ...view, pinned }]
        : old.filter((v) => v.id !== view.id)
      : old,
  );
  try {
    await api<ViewDto>(`/api/v1/views/${encodeURIComponent(view.id)}/pin`, { method: "PUT", body: { pinned } });
  } finally {
    void refreshViews();
  }
}

export async function reorderPinned(ids: string[]): Promise<void> {
  queryClient.setQueryData<ViewDto[]>(pinnedViewsQuery.queryKey, (old) => {
    if (!old) return old;
    const byId = new Map(old.map((v) => [v.id, v]));
    return ids.map((id) => byId.get(id)).filter((v): v is ViewDto => !!v);
  });
  try {
    const fresh = await api<ViewDto[]>("/api/v1/views/pinned/order", { method: "PUT", body: { ids } });
    queryClient.setQueryData(pinnedViewsQuery.queryKey, fresh);
  } catch (e) {
    void refreshViews();
    throw e;
  }
}

export async function validateViewQuery(
  query: string,
  scope: { scope: "personal" | "product" | "space"; scopeId?: string | null },
): Promise<QueryWarning[]> {
  const r = await api.post<{ warnings: QueryWarning[] }>("/api/v1/views/validate", { query, ...scope });
  return r.warnings;
}

const ARCHIVE_BLOCKED = new Set<Permission>([
  "item.create",
  "item.edit",
  "item.transition",
  "item.delete",
  "comment.create",
  "comment.moderate",
  "milestone.manage",
]);

/** Permission checks for gating UI (GET /me/permissions, shared cache). The server stays the judge. */
export function usePermissions() {
  const q = useQuery(permissionsQuery);
  const data = q.data;
  return {
    isPending: q.isPending,
    instance: (p: Permission) => !!data?.instance.includes(p),
    space: (p: Permission, spaceId: string | undefined) => !!spaceId && !!data?.spaces[spaceId]?.includes(p),
    // Archived products are read-only: the content permissions are off there (product.admin stays, to unarchive).
    product: (p: Permission, productId: string | undefined) =>
      !!productId &&
      !!data?.products[productId]?.includes(p) &&
      !(ARCHIVE_BLOCKED.has(p) && getCatalog()?.isArchived(productId)),
    /** Any scope at all grants `p` (admin entry points). */
    anywhere: (p: Permission) =>
      !!data &&
      (data.instance.includes(p) ||
        Object.values(data.spaces).some((l) => l.includes(p)) ||
        Object.values(data.products).some((l) => l.includes(p))),
  };
}
